import useCart from 'hooks/shop/useCart';
import useSWR from 'swr';
import { useEffect, useRef, useState } from 'react';
import useUpdateItemQuantity from 'hooks/shop/useUpdateItemQuantity';

export const parseDiscountsConditions = discounts => {
  const allDiscounts = (!Array.isArray(discounts) ? [discounts] : discounts).filter(
    d => d?.data?.status === 'ACTIVE'
  );

  return allDiscounts?.map(discount => {
    const discountTitle = discount?.data?.title;
    const discountId = discount?.discountId;
    const condition = discount?.data?.customerBuys?.items;
    const value = discount?.data?.customerBuys?.value;
    const usesPerOrderLimit = discount?.data?.usesPerOrderLimit;
    const freeProducts = discount?.data?.customerGets?.items?.products?.edges.map(({ node }) => {
      const { id, title, featuredImage, handle } = node || {};
      const variantId = node?.variants?.edges?.[0]?.node?.id;
      return { id, variantId, title, featuredImage, handle };
    });

    if (condition?.type === 'DiscountProducts') {
      const products = condition?.products?.edges?.map(({ node }) => node?.id);
      return { discountTitle, discountId, products, value, freeProducts, usesPerOrderLimit };
    }

    if (condition?.type === 'DiscountCollections') {
      const collections = condition?.collections?.edges?.map(({ node }) => node?.id);
      const collectionTitles = condition?.collections?.edges?.map(({ node }) => node?.title);
      return {
        discountTitle,
        discountId,
        collections,
        collectionTitles,
        value,
        freeProducts,
        usesPerOrderLimit,
      };
    }

    if (condition?.type === 'AllDiscountItems') {
      return { discountTitle, discountId, allItems: true, value, freeProducts };
    }

    return null;
  });
};

const nrOfCondicionItemsInCart = (condition, lineItems) =>
  lineItems?.reduce((acc, { node }) => {
    if (!condition) return acc;
    // Only count items added as free items (have _freeItem attribute),
    // not paid copies of the same variant the customer bought themselves.
    const isFreeItem = node?.attributes?.some(({ key }) => key === '_freeItem');
    if (!isFreeItem) return acc;
    const lineItemVariantId = node?.merchandise?.id;
    const lineItemQuantity = node?.quantity;
    if (condition.freeProducts.some(({ variantId }) => variantId === lineItemVariantId)) {
      return acc + lineItemQuantity;
    }
    return acc;
  }, 0);

const getFreeProductsByCondition = (conditions, lineItems) => {
  if (!conditions.length || !lineItems) {
    return {
      allFreeItems: null,
      unusedFreeItems: null,
      freeItemProgress: null,
    };
  }
  const allFreeItems = [];
  const unusedFreeItems = [];
  const freeItemProgress = [];

  conditions?.forEach(condition => {
    const neededQuantity = condition?.value?.quantity; // the quantity of items that should be in the cart to get the free item
    const neededAmount = condition?.value?.amount; // the amount of money that should be in the cart to get the free item
    // calculating the amount of line items and the total price of line items that satisfy the condition
    const lineItemsPassed = lineItems
      ?.filter(({ node }) => !node?.attributes.some(({ key }) => key === '_freeItem')) // exclude items that are already free items
      .reduce(
        (acc, { node }) => {
          if (!condition) return acc;
          const { quantity } = node;
          const componentCount = parseInt(node?.merchandise?.product?.componentCount?.value, 10);
          const effectiveQuantity = componentCount > 0 ? quantity * componentCount : quantity;
          const productId = node?.merchandise?.product.id;
          const lineItemCollections = node?.merchandise?.product?.collections?.edges?.map(
            ({ node: c }) => c?.id
          );
          const { products, collections, allItems } = condition;

          // if the condition is for all items, we take all items in account
          if (allItems) {
            acc.amount += effectiveQuantity;
            acc.totPrice += quantity * node?.merchandise?.priceV2?.amount;
          }

          // if the condition is for specific products, check if lineItem is one of them
          if (products?.includes(productId)) {
            acc.amount += effectiveQuantity;
            acc.totPrice += quantity * node?.merchandise?.priceV2?.amount;
          }

          // if the condition is for specific collections, check if lineItem is a part of condition collections
          if (collections?.some(c => lineItemCollections?.includes(c))) {
            acc.amount += effectiveQuantity;
            acc.totPrice += quantity * node?.merchandise?.priceV2?.amount;
          }

          return acc;
        },
        { amount: 0, totPrice: 0 }
      );

    // Track free item progress for quantity-based discounts
    if (neededQuantity) {
      const currentQty = lineItemsPassed?.amount || 0;
      const remainingQuantity = Math.max(0, neededQuantity - currentQty);
      const isSatisfied = currentQty >= neededQuantity;
      freeItemProgress.push({
        type: 'quantity',
        discountTitle: condition.discountTitle,
        discountId: condition.discountId,
        neededQuantity,
        currentQuantity: currentQty,
        remainingQuantity,
        isSatisfied,
        freeProducts: condition.freeProducts,
        collectionTitles: condition.collectionTitles,
      });
    }

    // Track free item progress for amount-based discounts
    if (neededAmount) {
      const currentPrice = lineItemsPassed?.totPrice || 0;
      const remainingAmount = Math.max(0, parseFloat(neededAmount) - currentPrice);
      const isSatisfied = currentPrice >= parseFloat(neededAmount);
      freeItemProgress.push({
        type: 'amount',
        discountTitle: condition.discountTitle,
        discountId: condition.discountId,
        neededAmount: parseFloat(neededAmount),
        currentAmount: currentPrice,
        remainingAmount,
        isSatisfied,
        freeProducts: condition.freeProducts,
        collectionTitles: condition.collectionTitles,
      });
    }

    // if the amount of line items or the total price of line items is enough to satisfy the condition, add the free item from the condition to result
    if (
      (neededQuantity && neededQuantity <= lineItemsPassed?.amount) ||
      (neededAmount && neededAmount <= lineItemsPassed?.totPrice)
    ) {
      const timesSatisfied = Math.floor(
        // how many times the condition is satisfied
        neededQuantity
          ? lineItemsPassed?.amount / neededQuantity
          : lineItemsPassed?.totPrice / neededAmount
      );

      const conditionItemsInCart = nrOfCondicionItemsInCart(condition, lineItems);

      const limitPerOrder = condition?.usesPerOrderLimit;

      allFreeItems.push({ ...condition });
      if (
        conditionItemsInCart < timesSatisfied &&
        (!limitPerOrder || limitPerOrder > conditionItemsInCart)
      ) {
        unusedFreeItems.push({
          ...condition,
          itemsLeft: timesSatisfied - conditionItemsInCart,
        });
      }
    }
  });

  return {
    allFreeItems,
    unusedFreeItems,
    freeItemProgress,
  };
};

// Module-level lock: prevents duplicate cleanup when the hook is mounted in multiple components
let cleanupInProgress = false;

export function useDiscountFreeItem(idsArr, locale) {
  const { cart, isEmpty } = useCart();
  const [freeProducts, setFreeProducts] = useState([]);
  const [validFreeProducts, setValidFreeProducts] = useState(null);
  const [freeItemProgress, setFreeItemProgress] = useState([]);
  const [qualifyingProductIds, setQualifyingProductIds] = useState([]);
  const [qualifyingCollectionIds, setQualifyingCollectionIds] = useState([]);
  const updateItemQuantity = useUpdateItemQuantity();

  // Ref to always hold the latest cart lines — avoids stale closure in setTimeout
  const cartLinesRef = useRef(cart?.lines?.edges);
  cartLinesRef.current = cart?.lines?.edges;

  const fetcher = (...urls) => {
    const f = u => fetch(u).then(r => r.json());
    if (urls.length > 1) {
      return Promise.all(urls.map(f));
    }
    return f(urls);
  };

  const urls = idsArr.map(
    id => `/api/shopify/get-automatic-discount-by-id/?discountId=${id}&locale=${locale}`
  ); // fetching all available discounts by id
  const { data: discounts } = useSWR(urls, fetcher);

  useEffect(() => {
    const conditions = parseDiscountsConditions(discounts);
    const availableFreeItems = getFreeProductsByCondition(conditions, cart?.lines?.edges);
    setFreeProducts(availableFreeItems.unusedFreeItems);
    setValidFreeProducts(availableFreeItems.allFreeItems);
    setFreeItemProgress(availableFreeItems.freeItemProgress || []);

    // Extract qualifying product and collection IDs from all conditions
    const productIds = [];
    const collectionIds = [];
    conditions?.forEach(condition => {
      if (condition?.products) {
        productIds.push(...condition.products);
      }
      if (condition?.collections) {
        collectionIds.push(...condition.collections);
      }
    });
    setQualifyingProductIds([...new Set(productIds)]);
    setQualifyingCollectionIds([...new Set(collectionIds)]);
  }, [cart?.lines?.edges, isEmpty, discounts]);

  useEffect(() => {
    const timeout = setTimeout(async () => {
      if (cleanupInProgress) return;

      const freshLines = cartLinesRef.current;
      if (!freshLines || !validFreeProducts) return;

      const freeLineItems = freshLines.filter(({ node }) =>
        node?.attributes?.some(({ key }) => key === '_freeItem')
      );
      if (!freeLineItems.length) return;

      const itemsToRemove = freeLineItems.filter(({ node }) => {
        const variantId = node?.merchandise?.id;
        return !validFreeProducts.some(({ freeProducts }) =>
          freeProducts.some(fp => fp.variantId === variantId)
        );
      });
      if (!itemsToRemove.length) return;

      const lineItems = itemsToRemove.map(({ node }) => ({
        variantId: node?.merchandise?.id,
        id: node.id,
        quantity: 0,
      }));

      cleanupInProgress = true;
      try {
        await updateItemQuantity(lineItems);
      } catch {
        // Handled by useUpdateItemQuantity — cart will resync
      } finally {
        cleanupInProgress = false;
      }
    }, 1000);
    return () => clearTimeout(timeout);
  }, [cart?.lines?.edges, validFreeProducts]);

  return {
    discounts,
    freeProducts,
    freeItemProgress,
    qualifyingProductIds,
    qualifyingCollectionIds,
  };
}

// Helper function to check if a product is eligible for free item
export const isProductEligibleForFreeItem = (
  productId,
  productCollectionIds,
  qualifyingProductIds,
  qualifyingCollectionIds
) => {
  if (qualifyingProductIds?.includes(productId)) return true;
  if (productCollectionIds?.some(id => qualifyingCollectionIds?.includes(id))) return true;
  return false;
};

// Centralised label resolver – returns the Contentful label or null (never a hardcoded fallback)
export const getFreeItemLabelText = translations =>
  translations?.freeItem?.receiveGift || null;

export default useDiscountFreeItem;
